// What a wedged window leaves behind: main-thread stalls over a second, the first derived_inert
// of a session and a long-blocked composer reach PostHog; every composer block is traced.
import { reportUiFailure, shouldReport } from "$lib/report-failure.js";
import { uiTrace } from "$lib/desktop-cold-start-trace.js";

export const LONG_TASK_MS = 1000;
export const COMPOSER_BLOCKED_REPORT_MS = 10_000;
const INERT_REPORTED_KEY = "cyborg7-derived-inert-reported";

interface StallTelemetryDeps {
  /** The route pattern (`/workspace/[id]/session/[sessionId]`), never a filled-in path. */
  route: () => string | null;
  observer?: typeof PerformanceObserver | undefined;
  console?: Pick<Console, "warn">;
  storage?: Pick<Storage, "getItem" | "setItem"> | undefined;
  /** Where the person's first pointer or key press is heard (the window). */
  input?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  now?: () => number;
}

let installed = false;

export function installUiStallTelemetry(deps: StallTelemetryDeps): void {
  if (installed) return;
  installed = true;
  watchLongTasks(deps);
  watchDerivedInert(deps);
}

// A stall before the person's first press is the boot (bundle parse, first render) on a slow
// machine, not a fault: traced only. From that press on, one that lasts a second is reported.
function watchLongTasks({ route, observer, input, now }: StallTelemetryDeps): void {
  const Observer = observer ?? globalThis.PerformanceObserver;
  if (!Observer?.supportedEntryTypes?.includes("longtask")) return;
  const clock = now ?? (() => globalThis.performance.now());
  const target = input ?? globalThis;
  let pressedAt: number | undefined;
  const pressed = () => {
    pressedAt ??= clock();
    target.removeEventListener("pointerdown", pressed, true);
    target.removeEventListener("keydown", pressed, true);
  };
  target.addEventListener("pointerdown", pressed, true);
  target.addEventListener("keydown", pressed, true);
  new Observer((list) => {
    for (const entry of list.getEntries()) {
      if (entry.duration < LONG_TASK_MS) continue;
      const ms = Math.round(entry.duration);
      const boot = pressedAt === undefined || entry.startTime < pressedAt;
      uiTrace("long-task", { ms, boot });
      if (boot || !shouldReport("ui.long_task", 10_000)) continue;
      reportUiFailure(
        "ui.long_task",
        new Error(`main thread blocked for ${ms}ms`),
        { durationMs: ms, route: route() },
        { failureKind: "fault" },
      );
    }
  }).observe({ type: "longtask", buffered: true });
}

// Svelte prints derived_inert (prod: only its URL) and goes on with stale values.
function watchDerivedInert({
  route,
  console: target = globalThis.console,
  storage,
}: StallTelemetryDeps): void {
  const store = storage ?? sessionStorageOrUndefined();
  let reported = readFlag(store);
  const warn = target.warn.bind(target);
  target.warn = (...args: unknown[]) => {
    warn(...args);
    if (reported || !args.some((a) => typeof a === "string" && a.includes("derived_inert"))) return;
    reported = true;
    writeFlag(store);
    reportUiFailure(
      "svelte.derived_inert",
      new Error("Reading a derived belonging to a now-destroyed effect"),
      { route: route() },
      { failureKind: "fault" },
    );
  };
}

function sessionStorageOrUndefined(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    // intentional: storage blocked; once per page load instead
    return undefined;
  }
}

function readFlag(store: StallTelemetryDeps["storage"]): boolean {
  try {
    return store?.getItem(INERT_REPORTED_KEY) === "1";
  } catch {
    // intentional: storage blocked; once per page load instead
    return false;
  }
}

function writeFlag(store: StallTelemetryDeps["storage"]): void {
  try {
    store?.setItem(INERT_REPORTED_KEY, "1");
  } catch {
    // intentional: storage blocked; once per page load instead
  }
}

export type ComposerBlockReason = "sending" | "permission" | "dictation";

type TraceIds = Record<string, string | undefined>;

/** One composer's blocked spans: each start and end traced, a long one reported. */
export class ComposerBlockTracker {
  // Ids taken at the span's start: its composer may be gone (deriveds dead) when it ends.
  private open: { reason: ComposerBlockReason; since: number; ids: TraceIds } | undefined;

  constructor(private readonly now: () => number = Date.now) {}

  update(reason: ComposerBlockReason | undefined, ids: TraceIds): void {
    if (reason === this.open?.reason) return;
    this.end();
    if (!reason) return;
    this.open = { reason, since: this.now(), ids };
    uiTrace("composer-disabled", { reason, ...ids });
  }

  /** The composer went away: a span still open ends here. */
  dispose(): void {
    this.end();
  }

  private end(): void {
    if (!this.open) return;
    const { reason, since, ids } = this.open;
    this.open = undefined;
    const ms = this.now() - since;
    uiTrace("composer-enabled", { reason, ms, ...ids });
    if (reason !== "sending" || ms < COMPOSER_BLOCKED_REPORT_MS) return;
    reportUiFailure(
      "agent_composer.blocked_long",
      new Error(`composer blocked for ${ms}ms`),
      {
        ...ids,
        reason,
        durationMs: ms,
      },
      { failureKind: "fault" },
    );
  }
}

/** Test seam. */
export function __resetUiStallTelemetry(): void {
  installed = false;
}
